// Since we're using Vite (client-side), we'll import all markdown files directly
// This approach works well with Vite's static asset handling

// Import all markdown files using Vite's import.meta.glob
const markdownModules = import.meta.glob('../pages/posts/*.md', { as: 'raw' })

export interface BlogPost {
  id: string
  slug: string
  title: string
  date: string
  excerpt: string
  content: string
  tags: string[]
  readTime: string
}

// Parse frontmatter from markdown content
function parseFrontmatter(content: string) {
  const frontmatterRegex = /^---\n([\s\S]*?)\n---\n([\s\S]*)$/
  const match = content.match(frontmatterRegex)

  if (!match) {
    return {
      data: {},
      content
    }
  }

  const frontmatter = match[1]
  const markdownContent = match[2]

  // Parse YAML-like frontmatter
  const data: Record<string, any> = {}
  frontmatter.split('\n').forEach(line => {
    const [key, ...valueParts] = line.split(':')
    if (key && valueParts.length > 0) {
      const value = valueParts.join(':').trim()
      // Handle different data types
      if (value.startsWith('"') && value.endsWith('"')) {
        data[key.trim()] = value.slice(1, -1)
      } else if (value.startsWith('[') && value.endsWith(']')) {
        // Parse array
        data[key.trim()] = value.slice(1, -1).split(',').map(v => v.trim().replace(/['"]/g, ''))
      } else {
        data[key.trim()] = value
      }
    }
  })

  return { data, content: markdownContent }
}

// Convert markdown to HTML (basic implementation)
function markdownToHtml(markdown: string) {
  return markdown
    .replace(/^### (.*$)/gim, '<h3>$1</h3>')
    .replace(/^## (.*$)/gim, '<h2>$1</h2>')
    .replace(/^# (.*$)/gim, '<h1>$1</h1>')
    .replace(/\*\*(.*)\*\*/gim, '<strong>$1</strong>')
    .replace(/\*(.*)\*/gim, '<em>$1</em>')
    .replace(/!\[([^\]]*)\]\(([^)]*)\)/gim, '<img alt="$1" src="$2" />')
    .replace(/\[([^\]]*)\]\(([^)]*)\)/gim, '<a href="$2">$1</a>')
    .replace(/\n\n/gim, '</p><p>')
    .replace(/\n/gim, '<br>')
    .replace(/^/, '<p>')
    .replace(/$/, '</p>')
}

// Get all blog posts
export async function getAllBlogPosts(): Promise<BlogPost[]> {
  const posts: BlogPost[] = []

  for (const path in markdownModules) {
    try {
      const content = await markdownModules[path]()
      const fileName = path.split('/').pop()?.replace('.md', '') || ''

      const { data, content: markdownContent } = parseFrontmatter(content)
      const htmlContent = markdownToHtml(markdownContent)

      // Calculate read time (average reading speed: 200 words per minute)
      const wordCount = markdownContent.split(/\s+/).length
      const readTime = Math.ceil(wordCount / 200) + ' min read'

      posts.push({
        id: fileName,
        slug: fileName,
        title: data.title || fileName,
        date: data.date || new Date().toISOString().split('T')[0],
        excerpt: data.excerpt || '',
        content: htmlContent,
        tags: data.tags || [],
        readTime
      })
    } catch (error) {
      console.error(`Error loading blog post ${path}:`, error)
    }
  }

  // Sort posts by date (newest first)
  return posts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
}

// Get a single blog post by slug
export async function getBlogPostBySlug(slug: string): Promise<BlogPost | null> {
  const path = `../pages/posts/${slug}.md`

  if (!markdownModules[path]) {
    return null
  }

  try {
    const content = await markdownModules[path]()
    const { data, content: markdownContent } = parseFrontmatter(content)
    const htmlContent = markdownToHtml(markdownContent)

    const wordCount = markdownContent.split(/\s+/).length
    const readTime = Math.ceil(wordCount / 200) + ' min read'

    return {
      id: slug,
      slug,
      title: data.title || slug,
      date: data.date || new Date().toISOString().split('T')[0],
      excerpt: data.excerpt || '',
      content: htmlContent,
      tags: data.tags || [],
      readTime
    }
  } catch (error) {
    console.error(`Error loading blog post ${slug}:`, error)
    return null
  }
}

// Get all post slugs
export function getAllPostSlugs(): string[] {
  return Object.keys(markdownModules).map(path =>
    path.split('/').pop()?.replace('.md', '') || ''
  )
}